import React, {useState} from 'react';
import {
    Chip,
    Menu,
    MenuItem,
    Typography
} from "@mui/material";
import UserRole, {roleLabels} from "../../schemas/user/roles";
import {Add} from "@mui/icons-material";
import {useAuth} from "../../utils/hooks/useAuth";
import useSWR from "swr";
import {getFetcher} from "../../utils/api/fetchers";
import {useConfirm} from "material-ui-confirm";
import {useNotification} from "../../utils/hooks/useNotification";

interface RolesSelectorProps {
    selectedRoles: UserRole[],
    setRoles: (roles: UserRole[]) => void,
    readOnly?: boolean,
    confirmation?: boolean
}

const RolesSelector: React.FC<RolesSelectorProps> = ({
                                                         selectedRoles: roles,
                                                         setRoles,
                                                         readOnly = false,
                                                         confirmation = true
                                                     }) => {
    const {hasPermission} = useAuth();
    const {showNotification} = useNotification();
    const confirm = useConfirm();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const {data: availableRoles} = useSWR<UserRole[]>('/roles', getFetcher);
    const availableRolesToAdd = availableRoles ? availableRoles.filter((role) => !roles.includes(role)) : [];

    const handleRoleDelete = (role: UserRole) => {
        if (role === UserRole.USER) {
            showNotification("Nelze odebrat roli uživatele", "error");
            return;
        }
        if (confirmation) {
            confirm({
                description: `Opravdu chcete odebrat roli ${roleLabels[role] || role}?`,
                title: "Odebrání role",
                confirmationText: "Odebrat",
                cancellationText: "Zrušit",
                confirmationButtonProps: {variant: "contained", color: "error"},
            })
                .then(() => setRoles(roles.filter((r) => r !== role)))
                .catch(() => {
                });
        } else {
            setRoles(roles.filter((r) => r !== role));
        }
    };

    const handleRoleAdd = (newRole: UserRole) => {
        if (!roles.includes(newRole)) {
            setRoles([...roles, newRole]);
        }
        handleMenuClose();
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Typography>
                Role:
            </Typography>
            {roles && roles.length > 0 ? (
                roles.map((role) => (
                    <Chip
                        key={role}
                        color={role === 'ADMIN' ? 'error' : 'primary'}
                        label={roleLabels[role] || role}
                        sx={{margin: '5px'}}
                        onDelete={(
                            readOnly
                            || !hasPermission(UserRole.ADMIN)
                            || role === UserRole.USER
                        ) ? undefined : () => handleRoleDelete(role)}
                    />
                ))
            ) : (
                <Chip
                    label="Uživatel nemá žádné role"
                    sx={{margin: '5px'}}
                />
            )}
            {availableRolesToAdd.length > 0 && !readOnly && hasPermission(UserRole.ADMIN) && (
                <>
                    <Chip
                        label="Přidat roli"
                        icon={<Add/>}
                        sx={{margin: '5px'}}
                        color="primary"
                        variant="outlined"
                        onClick={handleMenuOpen}
                    />
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                    >
                        {availableRolesToAdd.map((role) => (
                            <MenuItem
                                key={role}
                                onClick={() => handleRoleAdd(role)}
                            >
                                {roleLabels[role] || role}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </>
    );
};

export default RolesSelector;